import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { toast } from 'sonner';
import { Plus, Search, ShieldCheck } from 'lucide-react';
import { Button, Input } from '@staff-app/ui';
import { type AdminRole, type AdminUser } from '@staff-app/shared-types';

/** Role filter chips, in display order. 'all' clears the role filter. */
const ROLE_FILTERS = ['all', 'root', 'engineer', 'support', 'readonly'] as const;
import { STAFF_ADMIN_USERS, STAFF_REENABLE_ADMIN_USER } from '@staff-app/graphql-client';
import { useAuthStore } from '@/stores/auth-store';
import { PendingApprovalPage } from '@/pages/PendingApprovalPage';
import { AdminUsersTable } from '../components/AdminUsersTable';
import { InviteUserDialog } from '../components/InviteUserDialog';
import { EditRoleDialog } from '../components/EditRoleDialog';
import { DisableUserDialog } from '../components/DisableUserDialog';

interface ReenableResult {
  staffReenableAdminUser: AdminUser;
}

/**
 * Admin Users — root-only CRUD.
 *
 * Upstream routing guard: `<AdminRoleRoute requiredRole="root">` already
 * blocks non-root users. This component does a defensive re-check and
 * renders `<PendingApprovalPage />` if somehow a non-root user reaches it.
 */
export function AdminUsersPage() {
  const me = useAuthStore((s) => s.adminUser);

  const { data, loading, error } = useQuery<{ staffAdminUsers: AdminUser[] }>(STAFF_ADMIN_USERS, {
    fetchPolicy: 'cache-and-network',
  });

  const [reenableMutation] = useMutation<ReenableResult>(STAFF_REENABLE_ADMIN_USER, {
    refetchQueries: ['staffAdminUsers'],
  });

  const [inviteOpen, setInviteOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [disablingUser, setDisablingUser] = useState<AdminUser | null>(null);
  const [roleFilter, setRoleFilter] = useState<AdminRole | 'all'>('all');
  const [search, setSearch] = useState('');

  // Defensive: if somehow a non-root user reaches this page (e.g. direct URL),
  // render the pending-approval view instead of leaking data + actions.
  if (!me?.roles?.includes('root')) {
    return <PendingApprovalPage />;
  }

  // GraphQL serializes the AdminRole enum as its KEY (uppercase: ROOT,
  // ENGINEER, …) while the whole web side compares lowercase. Normalize ONCE
  // here so the rollup, table badges, and EditRoleDialog preselect all see lowercase.
  const users = (data?.staffAdminUsers ?? []).map((u) => ({
    ...u,
    roles: (u.roles ?? []).map((r) => r.toLowerCase() as AdminRole),
  }));
  const rollup = summarizeRoles(users);

  // Client-side filter: the page already loads every admin user, so role +
  // email search are in-memory over `users`. The rollup counts stay on the full
  // set so the chips always show totals regardless of the active filter.
  const q = search.trim().toLowerCase();
  const filtered = users.filter((u) => {
    const matchesRole = roleFilter === 'all' || (u.roles ?? []).includes(roleFilter);
    // Search matches the user's email ONLY — not `invitedBy` — so e.g. searching
    // "paul@optisigns.com" returns Paul's row, not everyone Paul invited.
    const matchesSearch = !q || u.email.toLowerCase().includes(q);
    return matchesRole && matchesSearch;
  });

  const handleReenable = async (user: AdminUser) => {
    try {
      await reenableMutation({ variables: { email: user.email } });
      toast.success('User re-enabled');
    } catch (err) {
      const msg = (err as { message?: string })?.message ?? 'Unknown error';
      toast.error('Re-enable failed', { description: msg });
    }
  };

  return (
    <div className="flex flex-col gap-4 p-6" data-testid="admin-users-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
          <ShieldCheck className="h-6 w-6" />
          {'Admin Users'}
        </h1>
          <p className="mt-1 text-sm text-muted-foreground" data-testid="admin-users-rollup">
            {users.length} {'users'} · {rollup.root} {'root'} · {rollup.engineer}{' '}
            {'engineer'} · {rollup.support} {'support'} · {rollup.readonly} {'readonly'}
          </p>
        </div>
        <Button
          onClick={() => setInviteOpen(true)}
          className="cursor-pointer"
          data-testid="invite-user-button"
        >
          <Plus className="mr-2 h-4 w-4" />
          {'Invite user'}
        </Button>
      </div>

      {error ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {error.message}
        </div>
      ) : loading && !users.length ? (
        <div className="text-sm text-muted-foreground">{'Loading admin users…'}</div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 w-56 pl-7 text-sm"
                aria-label="Search admin users by email"
                data-testid="admin-users-search"
              />
            </div>
            <div className="flex flex-wrap gap-1" data-testid="admin-users-role-filter">
              {ROLE_FILTERS.map((r) => (
                <Button
                  key={r}
                  size="sm"
                  variant={roleFilter === r ? 'default' : 'outline'}
                  className="h-8 px-2.5 text-xs capitalize"
                  onClick={() => setRoleFilter(r)}
                  aria-pressed={roleFilter === r}
                  data-testid={`admin-users-role-${r}`}
                >
                  {r === 'all' ? 'All' : `${r} (${rollup[r as keyof typeof rollup]})`}
                </Button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div
              className="rounded-lg border bg-card py-12 text-center text-sm text-muted-foreground"
              data-testid="admin-users-no-match"
            >
              {'No users match your search/filter.'}
            </div>
          ) : (
            <div className="rounded-lg border bg-card">
              <AdminUsersTable
                users={filtered}
                currentEmail={me.email}
                onEditRole={setEditingUser}
                onDisable={setDisablingUser}
                onReenable={handleReenable}
              />
            </div>
          )}
        </>
      )}

      <InviteUserDialog open={inviteOpen} onOpenChange={setInviteOpen} />
      <EditRoleDialog user={editingUser} onOpenChange={(o) => !o && setEditingUser(null)} />
      <DisableUserDialog user={disablingUser} onOpenChange={(o) => !o && setDisablingUser(null)} />
    </div>
  );
}

function summarizeRoles(users: AdminUser[]) {
  const rollup = { root: 0, engineer: 0, support: 0, readonly: 0 };
  for (const u of users) {
    for (const r of u.roles ?? []) {
      if (r in rollup) rollup[r as keyof typeof rollup]++;
    }
  }
  return rollup;
}
